import React, { FormEvent, FunctionComponent, useEffect, useState } from "react";
import {
  Alert,
  Col,
  Container,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Row,
  Spinner,
} from "react-bootstrap";
import { APP_NAME } from "../../constants";
import { ApplicationState } from "../../store";
import { bindActionCreators, Dispatch } from "redux";
import { changePasswordByToken, isTokenValid, resetPassword } from "../../store/auth/actions";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { toastr } from "react-redux-toastr";
import { push } from "connected-react-router";
import { AxiosError } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { validatePasswordWithDOM } from "../../utils/PasswordValidation";

type StateProps = {
  isLoading: {
    resetPassword?: boolean;
    changePassword?: boolean;
    isTokenValid?: boolean;
  };
  resetPasswordResponse?: any;
};
type DispatchProps = {
  resetPassword: (email: string) => any;
  changePasswordByToken: (
    token: string,
    email: string,
    password: string
  ) => any;
  push: (location: string) => any;
  isTokenValid: (
    token: string,
    email: string,
  ) => any;
};

type MatchParams = {
  token?: string;
  resetEmail?: string;
};

type Props = RouteComponentProps<MatchParams> & StateProps & DispatchProps;

const RestorePassword: FunctionComponent<Props> = ({
  match: {
    params: { token, resetEmail },
  },
  isLoading,
  resetPassword,
  changePasswordByToken,
  isTokenValid,
  push,
  resetPasswordResponse
}) => {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [isPasswordFieldsValid, setIsPasswordFieldsValid] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [attributeType, setAttributeType] = useState("password");
  const [attributeConfirmType, setAttributeConfirmType] = useState("password");

  useEffect(() => {
    if(token && resetEmail) {
      isTokenValid(token!, resetEmail!).then(
        () => {
        },
        (err: string) => {
          toastr.error("Failed", err);
          push("/login")
        }
      );
    }
  },[token, resetEmail])

  useEffect(() => {
    const result = validatePasswordWithDOM(
      newPassword,
      newPasswordConfirm,
      "New Password and Confirm New Password fields do not match.",
      {
        newPasswordId: "new_password",
        newPasswordMsgId: "new_password_msg",
        confirmPasswordId: "confirm_new_password",
        confirmPasswordMsgId: "confirm_new_password_msg",
      }
    );
    setIsPasswordFieldsValid(result.isPasswordFieldsValid);
    setPasswordsMatch(result.passwordsMatch);
  }, [newPassword, newPasswordConfirm]);

  const handleEmailChange = (e: FormEvent<any>) => {
    e.preventDefault();
    const { value } = e.target as HTMLInputElement;
    setEmail(value);
  };

  const handleChange = (event: React.FormEvent<any>) => {
    const { name, value } = event.currentTarget;
    if(name == "newPassword") {
      setNewPassword(value);
      setPasswordsMatch(true);
    }
    if(name == "newPasswordConfirm") {
      setNewPasswordConfirm(value);
      setPasswordsMatch(true);
    }
  };

  const handleReset = (e: FormEvent<any>) => {
    e.preventDefault();
    resetPassword(email).then(
      () => setIsSent(true),
    ).catch(
      (err: string) =>
        toastr.error("Error", err)
    );
  };

  const handlePasswordChange = (e: FormEvent<any>) => {
    e.preventDefault();
    changePasswordByToken(token!, resetEmail!, newPassword).then(
      () => {
        toastr.success("Success", "Your password has been reset successfully.")
        push("/login")
      },
      (err: string) => toastr.error("Error", err)
    );
  };

  const passwordShow = () => {
    let attrType: string = attributeType === "password" ? "text": "password";
    setAttributeType(attrType)
  };

  const confirmPasswordShow = () => {
    let attrType: string = attributeConfirmType === "password" ? "text": "password";
    setAttributeConfirmType(attrType)
  };

  return (
    <div>
      <Container className="loginContainer">
        <Row className="align-items-center">
          <Col sm>
            <h1 className="hMax">
              Sign up for <strong>{APP_NAME}</strong>
            </h1>
            <br />
            <p className="text-white text-center mb-1">
              You can sign up your district for <strong>{APP_NAME}</strong>
            </p>
            <p className="text-white text-center">
              Once you have an account you'll be able to invite your staff and
              begin coaching.
            </p>
            <hr />
            <h2 className="text-white text-center">Have questions?</h2>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <button 
                className="blueBtnMd m-2" 
                onClick={() => window.open("https://readycoach.zendesk.com", "_blank", "noopener,noreferrer")}
              >Contact Us Today</button>
            </div>
          </Col>
          <Col sm>
            <div className="welcomeInputContainer">
              {isSent ? (
                <Alert data-cy="forgot-password-success" variant="success">
                  {resetPasswordResponse ? resetPasswordResponse?.status : `An email with your confirmation code has been sent to you.
                  Please check the email and follow the attached link.`}
                </Alert>
              ) : token ? (
                <Form onSubmit={handlePasswordChange}>
                  <h2 className="mb-3 font-weight-bold">Set New Password</h2>
                  <FormGroup>
                    <FormLabel column={false} className="font-weight-bold">
                      New Password
                    </FormLabel>
                    <div className="input-container">
                      <FormControl
                        required
                        minLength={8}
                        maxLength={20}
                        type={attributeType}
                        placeholder="New Password"
                        aria-label="New Password"
                        value={newPassword}
                        name="newPassword"
                        onChange={handleChange}
                        id={`new_password`}
                        // pattern={'^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=\\[\\]{};:\'",.?/~`])[A-Za-z0-9!@#$%^&*()_+\\-=\\[\\]{};:\'",.?/~`]{8,20}$'}
                      />
                      <FontAwesomeIcon  
                        onClick={passwordShow}  
                        icon={attributeType == 'password' ? faEye : faEyeSlash} 
                        className="icon-eye"
                      />
                    </div>
                    <div id={`new_password_msg`} style={{ color: "red" }}></div>
                    <ul className="password-rules">
                      <li>At least 8 to at most 20 characters long</li>
                      <li>At least one uppercase letter (A-Z)</li>
                      <li>At least one lowercase letter (a-z)</li>
                      <li>At least one number (0-9)</li>
                      <li>At least one non-alphanumeric character (! @ # $ % ^ & * ( ) _ + - = [ ] { } : ; ' " , . ? / ~ `)</li>
                    </ul>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel column={false} className="font-weight-bold">
                      Confirm New Password
                    </FormLabel>
                    <div className="input-container restore-password-confirm">
                      <FormControl
                        required
                        minLength={8}
                        maxLength={20}
                        type={attributeConfirmType}
                        placeholder="Confirm New Password"
                        aria-label="Confirm New Password"
                        value={newPasswordConfirm}
                        name="newPasswordConfirm"
                        onChange={handleChange}
                        onKeyPress={(e: any) =>
                          e.key === "Enter" ? handlePasswordChange(e) : undefined
                        }
                        isInvalid={!passwordsMatch}
                        id={`confirm_new_password`}
                      />
                      <FontAwesomeIcon
                        onClick={confirmPasswordShow}
                        icon={attributeConfirmType == "password" ? faEye : faEyeSlash}
                        className="icon-eye"
                      />
                    </div>
                    <div id={`confirm_new_password_msg`} style={{color:"red"}}></div>
                    <Form.Control.Feedback type="invalid">
                      Passwords do not match.
                    </Form.Control.Feedback>
                  </FormGroup>
                  <div className="d-flex justify-content-between">
                    <button
                      className="whiteBtnSm"
                      onClick={() => push("/login")}
                    >
                      Cancel
                    </button>
                    <button className="blueBtnSm" type="submit" disabled={!isPasswordFieldsValid}>
                      Change Password
                      {isLoading.changePassword && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="ml-1"
                        />
                      )}
                    </button>
                  </div>
                </Form>
              ) : (
                <div>
                  <h2 className="mb-3 font-weight-bold">Forgot Password</h2>
                  <p>
                    Give us your email address and instructions to reset your
                    password will be emailed to you.
                  </p>
                  <form id="resetPasswordForm" onSubmit={handleReset}>
                    <Form.Control
                      required
                      type="email"
                      placeholder="Enter email"
                      onChange={handleEmailChange}
                    />
                  </form>
                  <div className="d-flex justify-content-between mt-3">
                    <button
                      className="whiteBtnSm"
                      onClick={() => push("/login")}
                    >
                      Cancel
                    </button>
                    <button
                      className="blueBtnSm"
                      form="resetPasswordForm"
                      data-cy="reset-password-submit"
                    >
                      Reset my password
                      {isLoading.resetPassword && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="ml-1"
                        />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const mapStateToProps = ({ auth }: ApplicationState): StateProps => {
  return {
    isLoading: {
      resetPassword: auth.isLoading.resetPassword,
      changePassword: auth.isLoading.changePassword,
      isTokenValid: auth.isLoading.isTokenValid
    },
    resetPasswordResponse: auth.resetPasswordResponse
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      resetPassword: resetPassword,
      changePasswordByToken: changePasswordByToken,
      isTokenValid: isTokenValid,
      push: push,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(RestorePassword);
